import PropTypes from "prop-types"
import React, { useContext, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import {
  ArchiveContactBoatAction,
  NewContractAction,
  NewReservationAction,
  RestoreContactBoatAction,
} from "src/actions/contact_boats"
import ContractGroupModal from "src/main/Contact/ContractGroupModal"
import { ContactsContext } from "src/main/Contact/index"

import BaseAvatar from "src/components/BaseAvatar"
import DataTableV2 from "src/components/DataTableV2"
import Tooltip from "src/components/Tooltip"

import { archiveContactBoat, restoreContactBoat } from "src/api/ContactBoat"

import { useToast } from "src/hooks/use_toast"

const STATUS_CONFIG = {
  active: {
    icon: "icon-check",
    iconClass: "text-teal-600",
    label: "Active",
  },
  missing: {
    icon: "icon-question-circle-btl",
    iconClass: "font-bold text-gray-500",
    label: "Missing",
  },
  expired: {
    icon: "icon-exclamation-circle",
    iconClass: "font-semibold text-red-600",
    label: "Expired",
  },
}

const renderStatusIcon = (status) => {
  const config = STATUS_CONFIG[status]
  if (!config) return null

  return (
    <div className="w-min">
      <Tooltip text={config.label} placement="left" variant="dark">
        <i className={`icon ${config.icon} text-lg ${config.iconClass}`} />
      </Tooltip>
    </div>
  )
}

const renderStatusInline = (status) => {
  const config = STATUS_CONFIG[status]
  if (!config) return null

  return (
    <span className="inline-flex items-center gap-1">
      <i className={`icon ${config.icon} text-sm ${config.iconClass}`} />
      <span className="text-xs text-gray-600">{config.label}</span>
    </span>
  )
}

const COLUMNS = [
  {
    key: "name",
    header: "Boat",
    sortKey: "name",
    sortable: true,
    cardRole: "title",
    render: (row) => (
      <div className="flex items-center space-x-2">
        <BaseAvatar
          name={row.name}
          src={row.thumbnailUrl}
          backgroundColor={row.avatarBackgroundColor}
        />
        <span>{row.name}</span>
      </div>
    ),
  },
  {
    key: "contactName",
    header: "Captain",
    sortKey: "contact_name",
    sortable: true,
  },
  {
    key: "boatType",
    header: "Type",
    sortKey: "type",
    sortable: true,
  },
  {
    key: "lengthOverall",
    header: "LOA",
    sortKey: "length_overall",
    sortable: true,
    width: "75px",
  },
  {
    key: "beam",
    header: "Beam",
    sortKey: "beam",
    sortable: true,
    width: "75px",
  },
  {
    key: "draw",
    header: "Draft",
    sortKey: "draw",
    sortable: true,
    width: "75px",
  },
  {
    key: "height",
    header: "Height",
    sortKey: "height",
    sortable: true,
    width: "75px",
  },
  {
    key: "insuranceStatus",
    header: "Ins. Status",
    width: "100px",
    render: (row) => renderStatusIcon(row.insuranceStatus),
    cardRender: (row) => renderStatusInline(row.insuranceStatus),
  },
  {
    key: "registrationStatus",
    header: "Reg. Status",
    width: "100px",
    render: (row) => renderStatusIcon(row.registrationStatus),
    cardRender: (row) => renderStatusInline(row.registrationStatus),
  },
  {
    key: "registrationNumber",
    header: "Reg. Number",
    width: "110px",
  },
  {
    key: "hullIdentification",
    header: "Hull ID",
    width: "130px",
    hide: true,
  },
]

const ContactBoatsTable = ({ table, onPrint, status }) => {
  const { contractGroups } = useContext(ContactsContext)
  const [showModal, setShowModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  const [selectedContactBoat, setSelectedContactBoat] = useState(null)
  const queryClient = useQueryClient()
  const showToast = useToast()
  const isActive = status === "active"

  const { mutate } = useMutation(
    (encodedId) =>
      isActive ? archiveContactBoat(encodedId) : restoreContactBoat(encodedId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("contactBoats")
        showToast(data.message, { type: "success" })
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const activeActions = [
    NewReservationAction(),
    NewContractAction({
      onClickSingle: (row) => {
        setSelectedContact(row.contactEncodedId)
        setSelectedContactBoat(row.encodedId)
        setShowModal(true)
      },
    }),
    ArchiveContactBoatAction({
      onClickSingle: (row) => mutate(row.encodedId),
    }),
  ]

  const archivedActions = [
    RestoreContactBoatAction({
      onClickSingle: (row) => mutate(row.encodedId),
    }),
  ]

  if (table.isError) {
    return (
      <div className="text-muted p-5 text-center">
        <h3 className="mb-5 text-lg font-semibold">Error loading boats</h3>
      </div>
    )
  }

  return (
    <>
      <DataTableV2
        name="contact-boats"
        columns={COLUMNS}
        data={table.records}
        actions={isActive ? activeActions : archivedActions}
        rowConfig={{ getViewUrl: (row) => row.linkPath }}
        rowCount={15}
        maxHeight="none"
        emptyState={
          <div className="text-muted p-5 text-center">
            <h3 className="mb-5 text-lg font-semibold">No boats found</h3>
          </div>
        }
        onPrint={onPrint}
        {...table.tableProps}
      />
      <ContractGroupModal
        contractGroups={contractGroups}
        isOpen={showModal}
        selectedContact={selectedContact}
        selectedContactBoat={selectedContactBoat}
        setIsOpen={setShowModal}
        setSelectedContact={setSelectedContact}
        setSelectedContactBoat={setSelectedContactBoat}
      />
    </>
  )
}

ContactBoatsTable.propTypes = {
  table: PropTypes.object.isRequired,
  onPrint: PropTypes.func,
  status: PropTypes.string.isRequired,
}

export default ContactBoatsTable
